/**
 * Motivos fixos de cancelamento de contrato (ContractDraft).
 * O texto enviado na API é gravado em PlaceholdersDataJson (`_erpContractCancelHistory` em array ou legado `_erpContractCancel`).
 * B2B reutiliza os mesmos rótulos no fluxo de Observations.
 */
export enum ContractCancelReason {
  ClienteEncerramentoAntecipado = 'ClienteEncerramentoAntecipado',
  SolicitacaoCliente = 'SolicitacaoCliente',
  Inadimplencia = 'Inadimplencia',
  QuebraContrato = 'QuebraContrato',
  EncerramentoOperacao = 'EncerramentoOperacao',
  DescumprimentoClausulas = 'DescumprimentoClausulas',
  AcordoComercial = 'AcordoComercial',
  DevolucaoVeiculo = 'DevolucaoVeiculo',
  SubstituicaoContratual = 'SubstituicaoContratual',
  OutrosOperacionais = 'OutrosOperacionais'
}

export const CONTRACT_CANCEL_REASON_LABELS: Record<ContractCancelReason, string> = {
  [ContractCancelReason.ClienteEncerramentoAntecipado]: 'Cliente solicitou encerramento antecipado',
  [ContractCancelReason.SolicitacaoCliente]: 'Solicitação do cliente',
  [ContractCancelReason.Inadimplencia]: 'Cancelamento por inadimplência',
  [ContractCancelReason.QuebraContrato]: 'Quebra de contrato',
  [ContractCancelReason.EncerramentoOperacao]: 'Encerramento da operação',
  [ContractCancelReason.DescumprimentoClausulas]: 'Descumprimento de cláusulas',
  [ContractCancelReason.AcordoComercial]: 'Acordo comercial entre as partes',
  [ContractCancelReason.DevolucaoVeiculo]: 'Devolução do veículo',
  [ContractCancelReason.SubstituicaoContratual]: 'Substituição por novo contrato',
  [ContractCancelReason.OutrosOperacionais]: 'Outros motivos operacionais'
};

export const CONTRACT_CANCEL_REASON_SELECT_OPTIONS: { code: ContractCancelReason; label: string }[] = (
  Object.values(ContractCancelReason) as ContractCancelReason[]
).map((code) => ({ code, label: CONTRACT_CANCEL_REASON_LABELS[code] }));

export function contractCancelMotivoFromCode(code: string | null | undefined): string | undefined {
  if (code == null || code === '') return undefined;
  const label = CONTRACT_CANCEL_REASON_LABELS[code as ContractCancelReason];
  return label ?? undefined;
}

/** Nó reservado em PlaceholdersDataJson / espelho no contrato (lado API). */
export interface ErpContractCancelAudit {
  motivo: string;
  cancelledAtUtc: string;
}

/** Histórico em `placeholdersDataJson._erpContractCancelHistory` (array). */
export function parseErpContractCancelHistoryFromPlaceholdersJson(
  jsonStr: string | null | undefined
): ErpContractCancelAudit[] {
  if (!jsonStr?.trim()) return [];
  try {
    const obj = JSON.parse(jsonStr) as Record<string, unknown>;
    const raw = obj['_erpContractCancelHistory'];
    if (!Array.isArray(raw)) return [];
    const out: ErpContractCancelAudit[] = [];
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      const motivo = rec['motivo'] ?? rec['Motivo'];
      const cancelledAtUtc = rec['cancelledAtUtc'] ?? rec['CancelledAtUtc'];
      if (typeof motivo === 'string' && motivo.trim()) {
        out.push({
          motivo: motivo.trim(),
          cancelledAtUtc: typeof cancelledAtUtc === 'string' ? cancelledAtUtc : ''
        });
      }
    }
    return out;
  } catch {
    return [];
  }
}

export function parseErpContractCancelFromPlaceholdersJson(
  jsonStr: string | null | undefined
): ErpContractCancelAudit | null {
  if (!jsonStr?.trim()) return null;
  try {
    const obj = JSON.parse(jsonStr) as Record<string, unknown>;
    const history = parseErpContractCancelHistoryFromPlaceholdersJson(jsonStr);
    if (history.length > 0) {
      return history[history.length - 1];
    }
    const node = obj['_erpContractCancel'] as Record<string, unknown> | undefined;
    if (!node || typeof node !== 'object') return null;
    const motivo = node['motivo'] ?? node['Motivo'];
    const cancelledAtUtc = node['cancelledAtUtc'] ?? node['CancelledAtUtc'];
    if (typeof motivo !== 'string' || !motivo.trim()) return null;
    return {
      motivo: motivo.trim(),
      cancelledAtUtc: typeof cancelledAtUtc === 'string' ? cancelledAtUtc : ''
    };
  } catch {
    return null;
  }
}

/** Normaliza `cancellationHistory` vindo do GET completo do contrato. */
export function normalizeContractCancellationHistoryFromApi(raw: unknown): ErpContractCancelAudit[] {
  if (!Array.isArray(raw)) return [];
  const out: ErpContractCancelAudit[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const motivo = rec['motivo'] ?? rec['Motivo'];
    const cancelledAtUtc = rec['cancelledAtUtc'] ?? rec['CancelledAtUtc'];
    if (typeof motivo === 'string' && motivo.trim()) {
      out.push({
        motivo: motivo.trim(),
        cancelledAtUtc: typeof cancelledAtUtc === 'string' ? cancelledAtUtc : ''
      });
    }
  }
  return out;
}
